import axios, { AxiosError, AxiosRequestConfig, isAxiosError } from 'axios';
import { toaster } from '../components/ui/toaster';
// Add a global logout callback (to be set by AuthProvider)
// Use a mutable object to allow assignment from other modules
export const globalLogoutRef = { fn: null as null | (() => void) };

export const buildAuthWizardUrl = (path: string) => {
  const p = path.startsWith('/') ? path : `/${path}`;
  const authWizardBaseUrl = (import.meta.env.VITE_AUTH_WIZARD_URL || '').replace(/\/$/, '');
  return `${authWizardBaseUrl || '/auth-wizard'}${p}`;
};

export const buildDataPortalUrl = (path?: string) => {
  const p = path?.startsWith('/') ? path : `/${path}`;
  const dataPortalBaseUrl = (import.meta.env.VITE_DATA_PORTAL_URL || '').replace(/\/$/, '');
  return `${dataPortalBaseUrl || '/data-portal'}${p}`;
};

export const buildManagementConsoleAPIUrl = (path: string) => {
  const p = path.startsWith('/') ? path : `/${path}`;
  const managementConsoleBaseUrl = (import.meta.env.VITE_MANAGEMENT_CONSOLE_URL || '').replace(/\/$/, '');
  return `${managementConsoleBaseUrl || ''}${p}`;
};

export const DATA_REFRESH_TTL = 30_000;

const apiBase = (import.meta.env.BASE_URL || '').replace(/\/$/, '');

const api = axios.create({
  baseURL: '/',
  withCredentials: true,
});

// Prepend base path for /api/ requests (auth-wizard stays at root)
api.interceptors.request.use(config => {
  if (config.url?.startsWith('/api/')) {
    config.url = `${apiBase}${config.url}`;
  }
  return config;
});

let refreshPromise: Promise<void> | null = null;

const refreshToken = async () => {
  if (!refreshPromise) {
    refreshPromise = api
      .post(buildAuthWizardUrl('/refresh-token'), {}, { withCredentials: true })
      .then(() => {})
      .catch(error => {
        toaster.create({
          title: 'Error refreshing token',
          description: error.message,
          type: 'error',
        });
        return Promise.reject(error);
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error?.config as AxiosRequestConfig & { _retry?: boolean };
    const status = (error as AxiosError)?.response?.status;
    const url = originalRequest?.url || '';

    const isAuthEndpoint = url.includes('/auth-wizard/refresh-token');

    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        await refreshToken();
        return api(originalRequest);
      } catch (refreshErr) {
        // Call global logout if set
        if (globalLogoutRef.fn) globalLogoutRef.fn();
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

/** Reads `{ error: string }` (or similar) from axios response errors. */
export function apiErrorDescription(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as unknown;
    if (data && typeof data === 'object' && 'error' in data) {
      const err = (data as { error: unknown }).error;
      if (typeof err === 'string') {
        return err;
      }
      if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: unknown }).message === 'string'
      ) {
        return (err as { message: string }).message;
      }
    }
    if (data && typeof data === 'object' && 'message' in data) {
      const msg = (data as { message: unknown }).message;
      if (typeof msg === 'string' && msg !== '') {
        return msg;
      }
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Network error';
}
